import { useRef, useState } from 'react';
import { useUnmount } from 'react-use';

/** Represents the result form the useDelayedState hook. */
type UseDelayedStateResult<TState> = readonly [
  /** The updated state. */
  state: TState,
  /** The function to call to update the state. */
  update: (newState: TState) => void,
  /** The function to call to reset the state. */
  reset: () => void,
  /** The function to call to cancel updating the state. */
  cancel: () => void,
];

/**
 * A hook that lets you update a given state after the specified `delay`.
 *
 * @param initialState The initial state.
 * @param delay The delay in ms.
 */
export const useDelayedState = <TState>(
  initialState: TState,
  delay?: number,
): UseDelayedStateResult<TState> => {
  const [state, setState] = useState(initialState);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Updates the state after the specified `delay`. If another update is in
   * progress, it cancels it and creates a new one. If a `delay` is not
   * specified, it updates the state immediately.
   *
   * @param newState The new state.
   */
  const updateState = (newState: TState): void => {
    cancelPending();

    timeoutRef.current = setTimeout(() => {
      setState(newState);
      timeoutRef.current = null;
    }, delay ?? 0);
  };

  /** Cancels the ongoing state update, if any. */
  const cancelPending = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  /** Resets the state to the initial value and cancels any ongoing update. */
  const resetState = () => {
    cancelPending();
    setState(initialState);
  };

  /** Cancels any ongoing update when the component is unmounted. */
  useUnmount(() => {
    cancelPending();
  });

  return [state, updateState, resetState, cancelPending];
};
